import { utils } from '@kurtosys/ksys-app-template';
import { observable, computed, action } from 'mobx';
import { IDocument, IEntity, IExecutionOptions, IQueryOptions } from '../../../models/commonTypes';
import { IDocumentList } from '../models/IDocumentList';
import { StoreContext } from '../../../configuration/StoreContext';
import { IDocumentDownloadListProps } from '@kurtosys/ksys-app-components/dist/components/file/DocumentDownloadList/models/IDocumentDownloadListProps';
import { EntityStore } from './EntityStore';
import { EntityConsumerStore } from './EntityConsumerStore';
import { IDocumentDownloadItem } from '@kurtosys/ksys-app-components/dist/components/file/DocumentDownload/models/IDocumentDownloadItem';
import { IGroupedDocument } from '@kurtosys/ksys-app-components/dist/components/file/DocumentDownloadList/models/IGroupedDocument';
import { IDocumentListSort } from '../models/IDocumentListSort';
import { getExtension } from 'mime';
import { QueryStore } from './QueryStore';
import { IConfigurationComponentOrchestration } from '../../../models/app/IConfigurationComponentOrchestration';

const groupBy = utils.collection.groupBy;

export class DocumentListStore extends EntityConsumerStore {
	@observable.ref value: IDocumentList;
	@observable groupedDocumentsList: IGroupedDocument[];
	constructor(value: IDocumentList, storeContext: StoreContext, entityStore?: EntityStore) {
		super(storeContext, entityStore);
		this.value = { ...value };
		this.groupedDocumentsList = this.groupedDocuments;
	}

	async initialize() { }

	@computed
	get groupedDocuments(): IGroupedDocument[] {
		const {
			grouping = '',
			availableLabel = '{available} languages available',
			selectionLabel = '{selected}/{available}',
			documentSort = {
				key: 'label',
				direction: 'ASC',
			} as IDocumentListSort,
			groupedDocumentSortOrder = 'ASC',
			listItemLabel,
			fileName,
		} = this.value;

		const { queryStore, kurtosysApiStore } = this.storeContext;
		const { executionOptions } = queryStore;
		const { culture, fallbackCulture } = executionOptions;
		const { serviceUrl, token } = kurtosysApiStore;

		const sortCulture = culture || fallbackCulture || 'en-GB';

		const documentItemGroups: any = groupBy(this.documents, (document: any) => document._fields[grouping]);
		const documents: IGroupedDocument[] = Object.keys(documentItemGroups).map((key: string) => {
			const parsedKey = this.groupingLabelAlias(key);
			const documents: IDocumentDownloadItem<IDocument>[] = documentItemGroups[key].map((document: IDocument) => {
				const { clientCode } = document;
				const parsedLabel = this.getQueryValue(listItemLabel, document);
				const parsedFilename = this.getQueryValue(fileName, document);
				const extension = getExtension(document.fileType);
				const fullFileName = extension ? `${ parsedFilename }.${ extension }` : parsedFilename;
				const documentDownloadItem: IDocumentDownloadItem = {
					id: clientCode,
					label: parsedLabel,
					payload: document,
					selected: false,
					href: `${ serviceUrl }/documents/retrieveDocument/?token=${ token }&clientCode=${ clientCode }&filename=${ fullFileName }`,
				};
				return documentDownloadItem;
			});

			return {
				availableLabel,
				selectionLabel,
				label: parsedKey || key,
				documents: this.sortBy(documents, sortCulture, documentSort),
			};
		});

		const sortedGroupedDocuments = utils.collection.sortBy(documents, groupedDocument => groupedDocument.label, groupedDocumentSortOrder, sortCulture);
		return sortedGroupedDocuments;
	}

	private sortBy<T>(parsedDocuments: T[], sortCulture: string, sortBy?: IDocumentListSort): T[] {
		if (parsedDocuments.length > 0 && sortBy) {
			return utils.collection.sortBy(parsedDocuments, (parsedDocument: any) => {
				// we push the IDocumentDownloadItem<IDocument>.label onto the document base properties for default sorting rules. (label is not document base property, thus no overrides will occur)
				const document = { ...parsedDocument.payload, label: parsedDocument.label };
				const propertyContext = sortBy.isMetaKey ? document.meta : document;
				const property = propertyContext[sortBy.key];
				const propertyValue = sortBy.isMetaKey ? property && property.value : property;
				return propertyValue || ''; // default to blank string otherwise err: no .localCompare on undefined
			}, sortBy.direction, sortCulture);
		}
		return parsedDocuments;
	}

	@computed
	get props(): IDocumentDownloadListProps {
		const {
			breakpoints,
			allowSelection,
			heading,
			pillProps,
			hideWhenEmpty = false,
			hideLabel = false,
			dropdownLabelType = 'text',
			showPill = false,
			showCopyButton = false,
			copyButtonProps,
		} = this.value;
		const { appStore } = this.storeContext;
		const useSpinner = appStore.useSpinnerConfig;
		return {
			breakpoints,
			allowSelection,
			heading,
			hideLabel,
			dropdownLabelType,
			showPill,
			showCopyButton,
			copyButtonProps,
			pillProps,
			useSpinner,
			hideWhenEmpty: hideWhenEmpty || appStore.hideListsWhenNoDocuments,
			groupedDocuments: this.groupedDocumentsList,
			onDownloadClick: this.onClick,
			onItemToggle: this.onItemToggle,
		};
	}

	groupingLabelAlias(value: string): string | undefined {
		const { groupingLabelAliases } = this.value;
		if (groupingLabelAliases) {
			const alias = groupingLabelAliases.find(elem => elem.source === value);
			return alias && alias.replacement;
		}
	}

	@computed
	get documents(): IDocument[] {
		const response: IDocument[] = [];
		const { documentQueries } = this.value;
		const previousDocumentsById: { [key: string]: boolean } = {};
		if (documentQueries) {
			for (const documentQuery of documentQueries) {
				if (documentQuery) {
					const documents: IDocument[] = this.getQueryValue(documentQuery);
					if (!utils.typeChecks.isNullOrEmpty(documents)) {
						const uniqueDocuments = documents.filter(document => !previousDocumentsById[document.clientCode]);
						uniqueDocuments.forEach((document) => {
							const { clientCode } = document;
							previousDocumentsById[clientCode] = true;
						});
						response.push(...uniqueDocuments);
					}
				}
			}
		}
		return response;
	}

	@computed
	get documentIds(): string[] {
		return this.documents.map(document => document.clientCode);
	}

	@computed
	get hasDocuments(): boolean {
		return this.documents && this.documents.length > 0;
	}

	@computed
	get hasSelectedDocuments(): boolean {
		return this.selectedDocumentIds && this.selectedDocumentIds.length > 0;
	}

	@computed
	get selectedDocumentIds(): string[] {
		const documentIds: string[] = [];
		this.groupedDocumentsList.map(groupedDocument =>
			groupedDocument.documents.map(document =>
				document.selected && documentIds.push(document.id)));
		return documentIds;
	}

	onClick = async (payload: any): Promise<boolean> => {
		const { appStore } = this.storeContext;
		const { isLoading, saveDocument } = appStore.downloadStore;

		if (!isLoading) {
			const {
				fileName = { value: 'NO NAME' } as IQueryOptions,
			} = this.value;

			await saveDocument(payload, fileName);
		}

		return !isLoading;
	}

	@action
	onItemToggle = (id: any, selected: boolean) => {
		this.groupedDocumentsList = (this.groupedDocumentsList || []).map((groupedDocument: IGroupedDocument) => {
			for (const document of groupedDocument.documents) {
				if (document.id === id) {
					document.selected = selected;
				}
			}
			return groupedDocument;
		});
	}
}
